import React, { CSSProperties, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    GetAppealCode,
    getDonationInformation,
    getListAcknowledgmentFlagAsync,
    getListAppealCodeAsync,
    getListBatchCategoryAsync,
    getListFormCodeAsync,
    getListGiftTypeAsync,
    getLisTributeTypeAsync,
    getListServiceCenterAsync,
} from "../../../actions/makeAWish";
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@material-ui/core";
import Loading from "../../Loading";
import { IAppState } from "../../../types/stores";
import {
    AcknowledgmentFlag,
    AppealCode,
    DonationData,
    FormCode,
    GiftType,
    ServiceCenter,
    TributeType,
} from "../../../types/stores/makeAWish";
import { Autocomplete } from "@material-ui/lab";
import moment from "moment";
import { debounce } from "lodash";
import NumericalTextField from "../../material-components/Misc/NumericalTextField";

type Props = {
    id: string;
    batchNumber: string;
    sequenceNumber: string;
    onChange: (donation: DonationData | undefined, saveAllowed: boolean) => void;
    loadedData: boolean;
};

type Errors = {
    donationAmount: boolean;
    donationDate: boolean;
    depositDate: boolean;
    appealCodes: boolean;
    formCode: boolean;
    serviceCenter: boolean;
    ackFlag: boolean;
    giftType: boolean;
    zipCode: boolean;
};

const DonationDataForm: React.FC<Props> = ({ id, batchNumber, sequenceNumber, onChange, loadedData }: Props) => {
    const dispatch = useDispatch();
    const tableCell: CSSProperties = {
        fontSize: "16px",
        alignSelf: "center",
        fontFamily: "Roboto",
    };

    const defaultErrorState = {
        donationAmount: false,
        donationDate: false,
        depositDate: false,
        formCode: false,
        serviceCenter: false,
        ackFlag: false,
        giftType: false,
        zipCode: false,
        appealCodes: false,
    };

    const donationData = useSelector((state: IAppState) => state.makeAWish.donationData);
    const donorData = useSelector((state: IAppState) => state.makeAWish.editDonorsData[0]);
    const appealCodes = useSelector<IAppState, Array<AppealCode>>(state => state.makeAWish.appealCodes || []);
    const serviceCenters = useSelector<IAppState, Array<ServiceCenter>>(state => state.makeAWish.serviceCenters);
    const AckFlags = useSelector<IAppState, Array<AcknowledgmentFlag>>(state => state.makeAWish.acknowledgmentFlags);
    const formCodes = useSelector<IAppState, Array<FormCode>>(state => state.makeAWish.formCodes);
    const isDonationsLoading = useSelector((state: IAppState) => state.makeAWish.isDonationsLoading);
    const tributeTypes = useSelector<IAppState, Array<TributeType>>(state => state.makeAWish.tributeTypes);
    const giftTypes = useSelector<IAppState, Array<GiftType>>(state => state.makeAWish.giftTypes);

    const [appealCode, setAppealCode] = useState<string | null>(null);
    const [tributeType, setTributeType] = useState<string | null>(null);
    const [giftType, setGiftType] = useState<string | null>(null);
    const [hasData, setHasData] = useState<boolean>(false);
    const [selectedDonationDate, setDonationDate] = useState<string | null>(null);
    const [selectedDepositDate, setDepositDate] = useState<string | null>(null);
    const [donationAmount, setDonationAmount] = useState<string | null>(null);
    const [serviceCenter, setServiceCenter] = useState<string | null>(null);
    const [ackFlag, setAckFlag] = useState<string | null>(null);
    const [formCode, setFormCode] = useState<string | null>(null);
    const [zipCode, setZipCode] = useState<string | null>(null);
    const [assocIdent, setAssocIdent] = useState<string | null>(null);
    const [state, setState] = useState<DonationData>({});
    const [errors, setErrors] = useState<Errors>(defaultErrorState);

    const validate = (data: DonationData) => {
        let newErrors = { ...errors };

        if (!data.DONATION_AMOUNT) {
            newErrors.donationAmount = true;
        } else {
            newErrors.donationAmount = false;
        }

        if (!data.DONATION_DATE) {
            newErrors.donationDate = true;
        } else {
            newErrors.donationDate = false;
        }

        if (!data.DEPOSIT_DATE) {
            newErrors.depositDate = true;
        } else {
            newErrors.depositDate = false;
        }

        // no appeal code typed in
        // saved state appeal code doesn't match user typed appeal code
        // AND the typed appeal code isn't in the list of appeal codes
        if (
            !data.APPEAL_CODE ||
            (appealCode != data.APPEAL_CODE &&
                appealCodes &&
                appealCodes.filter(x => x.Code == data.APPEAL_CODE).length == 0)
        ) {
            newErrors.appealCodes = true;
        } else {
            newErrors.appealCodes = false;
        }

        if (!data.FORM_CODE) {
            newErrors.formCode = true;
        } else {
            newErrors.formCode = false;
        }

        if (!data.SERVICE_CENTER) {
            newErrors.serviceCenter = true;
        } else {
            newErrors.serviceCenter = false;
        }

        if (!data.ACKNOWLEDGMENT_FLAG) {
            newErrors.ackFlag = true;
        } else {
            newErrors.ackFlag = false;
        }

        if (!data.GIFT_TYPE) {
            newErrors.giftType = true;
        } else {
            newErrors.giftType = false;
        }

        if (!data.ZIP_CODE || data.ZIP_CODE.length != 5) {
            newErrors.zipCode = true;
        } else {
            newErrors.zipCode = false;
        }

        setErrors(newErrors);

        return !Object.values(newErrors).some(val => val === true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    };

    const onUpdate = (donation: DonationData) => {
        const canSave = validate(donation);
        onChange(donation, canSave);
    };

    useEffect(() => {
        setHasData(loadedData);
        if (Object.keys(donationData).length == 0 && !hasData) {
            setHasData(true);
            dispatch(GetAppealCode([]));
            dispatch(getListServiceCenterAsync());
            dispatch(getListAcknowledgmentFlagAsync());
            dispatch(getListFormCodeAsync());
            dispatch(getLisTributeTypeAsync());
            dispatch(getListGiftTypeAsync());
            dispatch(getListBatchCategoryAsync());
            setAppealCode("");
            dispatch(getDonationInformation(id, batchNumber, sequenceNumber)); // left
        }
    }, [id, batchNumber, sequenceNumber, donationData, hasData, dispatch, loadedData]);

    const handleChange = (name, value) => {
        setState({ ...state, [name]: value });
        onUpdate({ ...state, [name]: value });
    };

    useEffect(() => {
        if (JSON.stringify(donationData) !== "{}") {
            setState(donationData);
            setAppealCode(donationData.APPEAL_CODE || "");
        }
    }, [donationData]);

    const getListAppealCodeDebounced = debounce(newVal => {
        dispatch(getListAppealCodeAsync(newVal));
    }, 500);

    const handleChangeAutocomplete = newVal => {
        if (typeof newVal != "undefined") {
            setAppealCode(newVal);
            validate({ ...state, APPEAL_CODE: newVal });
            if (newVal.length >= 1) {
                getListAppealCodeDebounced(newVal);
            }
        }
    };

    return (
        <div className="row m-t">
            <div className="col-sm-16 text-nowrap" style={{ height: "100%", width: "1000px" }}>
                {isDonationsLoading ? (
                    <Loading />
                ) : JSON.stringify(donationData) !== "{}" ? (
                    <div>
                        <label>Master Info</label>
                        <TableContainer style={{ maxHeight: "68vh" }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Id</TableCell>
                                        <TableCell style={{ ...tableCell }}>First Name</TableCell>
                                        <TableCell style={{ ...tableCell }}>Surname</TableCell>
                                        <TableCell style={{ ...tableCell }}>City</TableCell>
                                        <TableCell style={{ ...tableCell }}>State</TableCell>
                                        <TableCell style={{ ...tableCell }}>Country</TableCell>
                                        <TableCell style={{ ...tableCell }}>Company</TableCell>
                                        <TableCell style={{ ...tableCell }}>Zip</TableCell>
                                        <TableCell style={{ ...tableCell }}>Email</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>{donorData.Id}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donorData.FirstName}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donorData.LastName}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donorData.City}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donorData.State}</TableCell>
                                        <TableCell style={{ ...tableCell }}>USA</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donorData.Company}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donationData.ZIP_CODE}</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donorData.Email}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <br />
                        <label>Tribute/Honoree Info</label>
                        <TableContainer style={{ maxHeight: "68vh" }}>
                            <Table>
                                <TableBody>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Id:</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donationData.IDENT_NUMBER}</TableCell>
                                        <TableCell style={{ ...tableCell }}>First Name:</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donationData.FIRST_NAME}</TableCell>
                                        <TableCell style={{ ...tableCell }}>Surname:</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donationData.SURNAME}</TableCell>
                                        <TableCell style={{ ...tableCell }}>City:</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donationData.CITY_NAME}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <br />
                        <label>Donation Info</label>
                        <TableContainer style={{ maxHeight: "68vh" }}>
                            <Table stickyHeader>
                                <TableBody>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Batch Number:</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donationData.BATCH_NUMBER}</TableCell>
                                        <TableCell style={{ ...tableCell }}>Sequence Number:</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donationData.SEQ_NUMBER}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Donation Date:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            <TextField
                                                id="DONATION_DATE"
                                                label="Donation Date"
                                                type="date"
                                                name="DONATION_DATE"
                                                value={
                                                    selectedDonationDate != null
                                                        ? selectedDonationDate
                                                        : donationData && donationData.DONATION_DATE
                                                        ? moment(donationData.DONATION_DATE, "MM/DD/YYYY")
                                                              .format("YYYY-MM-DD")
                                                              .toString()
                                                        : ""
                                                }
                                                onChange={e => {
                                                    setDonationDate(e.target.value);
                                                    handleChange(e.target.name, e.target.value);
                                                }}
                                                style={{ width: "100%" }}
                                                InputLabelProps={{ shrink: true }}
                                                error={errors.donationDate}
                                                required
                                            />
                                        </TableCell>
                                        <TableCell style={{ ...tableCell }}>Appeal Code:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            <FormControl style={{ width: "100%" }} error={errors.appealCodes} required>
                                                <Autocomplete
                                                    id="Appeal_Code"
                                                    options={appealCodes}
                                                    onInputChange={(_, value, reason) => {
                                                        if (reason !== "reset") handleChangeAutocomplete(value);
                                                    }}
                                                    onChange={(_e, value) => {
                                                        setAppealCode((value?.Code as string) || "");
                                                        handleChange("APPEAL_CODE", value?.Code as string);
                                                    }}
                                                    getOptionLabel={option => option.Code as string}
                                                    renderOption={option => (
                                                        <React.Fragment>
                                                            {option.Code} {option.MailingDate} {option.GiftType}{" "}
                                                            {option.MailingDesc} {option.Classification}{" "}
                                                            {option.LetterTxtDesc} {option.MonetaryListDesc}{" "}
                                                            {option.FrequencyDesc} {option.RecencyDesc}
                                                        </React.Fragment>
                                                    )}
                                                    {...{
                                                        inputValue:
                                                            appealCode != null
                                                                ? appealCode
                                                                : donationData && donationData.APPEAL_CODE
                                                                ? donationData.APPEAL_CODE
                                                                : "",
                                                    }}
                                                    renderInput={params => (
                                                        <TextField
                                                            {...params}
                                                            label="Search Appeal Code"
                                                            name="Appeal Code"
                                                            variant="standard"
                                                            helperText={"Type here to search for appeal code"}
                                                            error={errors.appealCodes}
                                                            required
                                                        />
                                                    )}
                                                />
                                            </FormControl>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Donation Amount:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            <NumericalTextField
                                                id="DONATION_AMOUNT"
                                                label="Donation Amount"
                                                numberType="decimal"
                                                name="DONATION_AMOUNT"
                                                value={
                                                    donationAmount != null
                                                        ? donationAmount
                                                        : donationData && donationData.DONATION_AMOUNT
                                                        ? donationData.DONATION_AMOUNT
                                                        : ""
                                                }
                                                onBlur={e => {
                                                    setDonationAmount(e.target.value);
                                                    handleChange(e.target.name, e.target.value as string);
                                                }}
                                                required
                                                error={errors.donationAmount}
                                                inputProps={{ maxLength: "256" }}
                                                fullWidth
                                            />
                                        </TableCell>
                                        <TableCell style={{ ...tableCell }}>Service Center:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            <FormControl fullWidth error={errors.serviceCenter} required>
                                                <InputLabel
                                                    htmlFor={`SERVICE_CENTER`}
                                                    error={errors.serviceCenter}
                                                    required
                                                >
                                                    Service Center
                                                </InputLabel>
                                                <Select
                                                    id={`SERVICE_CENTER`}
                                                    name="SERVICE_CENTER"
                                                    onChange={e => {
                                                        setServiceCenter(e.target.value as string);
                                                        handleChange(e.target.name, e.target.value as string);
                                                    }}
                                                    value={
                                                        serviceCenter != null
                                                            ? serviceCenter
                                                            : donationData && donationData.SERVICE_CENTER
                                                            ? donationData.SERVICE_CENTER
                                                            : ""
                                                    }
                                                    error={errors.serviceCenter}
                                                    required
                                                >
                                                    {serviceCenters.map(option => (
                                                        <MenuItem key={option.Service} value={option.Service}>
                                                            {option.Combined}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {errors.serviceCenter && (
                                                    <FormHelperText>A service center is required.</FormHelperText>
                                                )}
                                            </FormControl>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Form Code:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            <FormControl fullWidth error={errors.formCode} required>
                                                <InputLabel htmlFor={`FORM_CODE`} error={errors.formCode} required>
                                                    Form Code
                                                </InputLabel>
                                                <Select
                                                    id={`FORM_CODE`}
                                                    name="FORM_CODE"
                                                    onChange={e => {
                                                        setFormCode(e.target.value as string);
                                                        handleChange(e.target.name, e.target.value as string);
                                                    }}
                                                    required
                                                    error={errors.formCode}
                                                    value={
                                                        formCode != null
                                                            ? formCode
                                                            : donationData && donationData.FORM_CODE
                                                            ? donationData.FORM_CODE
                                                            : ""
                                                    }
                                                >
                                                    {formCodes.map(option => (
                                                        <MenuItem key={option.Code} value={option.Code}>
                                                            {option.Combined}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {errors.formCode && (
                                                    <FormHelperText>A form code is required.</FormHelperText>
                                                )}
                                            </FormControl>
                                        </TableCell>
                                        <TableCell style={{ ...tableCell }}>Gift Type:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            <FormControl fullWidth error={errors.giftType} required>
                                                <InputLabel htmlFor={`giftType`} error={errors.giftType} required>
                                                    Select Gift Type
                                                </InputLabel>
                                                <Select
                                                    id={`GIFT_TYPE`}
                                                    name="Gift Type"
                                                    onChange={e => {
                                                        setGiftType(e.target.value as string);
                                                        handleChange(e.target.name, e.target.value as string);
                                                    }}
                                                    value={
                                                        giftType != null
                                                            ? giftType
                                                            : donationData && donationData.GIFT_TYPE
                                                            ? donationData.GIFT_TYPE
                                                            : ""
                                                    }
                                                    error={errors.giftType}
                                                    required
                                                >
                                                    {giftTypes.map(option => (
                                                        <MenuItem key={option.Type} value={option.Type}>
                                                            {option.Combined}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Zip Code:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            {
                                                <TextField
                                                    id="ZIP_CODE"
                                                    label="Zip Code"
                                                    type="text"
                                                    name="ZIP_CODE"
                                                    value={
                                                        zipCode != null
                                                            ? zipCode
                                                            : donationData && donationData.ZIP_CODE
                                                            ? donationData.ZIP_CODE
                                                            : ""
                                                    }
                                                    onChange={e => {
                                                        const reg = new RegExp(/(^\d{0,5}$)/);
                                                        if (reg.test(e.target.value) || e.target.value == "") {
                                                            // ensure only numbers and empty strings save
                                                            setZipCode(e.target.value);
                                                            handleChange(e.target.name, e.target.value as string);
                                                        }
                                                    }}
                                                    required
                                                    inputProps={{ maxLength: "5" }}
                                                    style={{ width: "100%" }}
                                                    error={errors.zipCode}
                                                />
                                            }
                                        </TableCell>
                                        <TableCell style={{ ...tableCell }}> Tribute Type:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            <FormControl fullWidth>
                                                <InputLabel htmlFor={`tributeType`}>Select Tribute Type</InputLabel>
                                                <Select
                                                    id={`TRIBUTETYPE`}
                                                    name="TRIBUTE_TYPE"
                                                    onChange={e => {
                                                        setTributeType(e.target.value as string);
                                                        handleChange(e.target.name, e.target.value as string);
                                                    }}
                                                    value={
                                                        tributeType != null
                                                            ? tributeType
                                                            : donationData && donationData.TRIBUTE_TYPE
                                                            ? donationData.TRIBUTE_TYPE
                                                            : ""
                                                    }
                                                >
                                                    <MenuItem key={"none"} value={""}>
                                                        {"None"}
                                                    </MenuItem>
                                                    {tributeTypes.map(option => (
                                                        <MenuItem key={option.Type} value={option.Type}>
                                                            {option.Combined}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Associated Ident:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            <TextField
                                                id="ASSOC_IDENT"
                                                name="ASSOCIATED_IDENT"
                                                label="Associated Ident"
                                                type="text"
                                                value={
                                                    assocIdent != null
                                                        ? assocIdent
                                                        : donationData && donationData.ASSOCIATED_IDENT
                                                        ? donationData.ASSOCIATED_IDENT
                                                        : ""
                                                }
                                                onChange={e => {
                                                    const reg = new RegExp(/(^\d{0,9}$)/);
                                                    if (reg.test(e.target.value) || e.target.value == "") {
                                                        // ensure only numbers and empty strings save
                                                        setAssocIdent(e.target.value as string);
                                                        handleChange(e.target.name, e.target.value as string);
                                                    }
                                                }}
                                                style={{ width: "100%" }}
                                            />
                                        </TableCell>
                                        <TableCell style={{ ...tableCell }}>Ack Flag:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            <FormControl fullWidth error={errors.ackFlag} required>
                                                <InputLabel
                                                    htmlFor={`ACKNOWLEDGEMENT_FLAG`}
                                                    error={errors.ackFlag}
                                                    required
                                                >
                                                    Acknowledgment Flag
                                                </InputLabel>
                                                <Select
                                                    id={`ACKNOWLEDGEMENT_FLAG`}
                                                    name="ACKNOWLEDGMENT_FLAG"
                                                    onChange={e => {
                                                        setAckFlag(e.target.value as string);
                                                        handleChange(e.target.name, e.target.value as string);
                                                    }}
                                                    required
                                                    error={errors.ackFlag}
                                                    value={
                                                        ackFlag != null
                                                            ? ackFlag
                                                            : donationData && donationData.ACKNOWLEDGMENT_FLAG
                                                            ? donationData.ACKNOWLEDGMENT_FLAG
                                                            : ""
                                                    }
                                                >
                                                    {AckFlags.map(option => (
                                                        <MenuItem key={option.Flag} value={option.Flag}>
                                                            {option.Combined}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Deposit Date:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            <TextField
                                                id="depositdate"
                                                label="Deposit Date"
                                                name="DEPOSIT_DATE"
                                                type="date"
                                                value={
                                                    selectedDepositDate != null
                                                        ? selectedDepositDate
                                                        : donationData && donationData.DEPOSIT_DATE
                                                        ? moment(donationData.DEPOSIT_DATE, "MM/DD/YYYY")
                                                              .format("YYYY-MM-DD")
                                                              .toString()
                                                        : ""
                                                }
                                                onChange={e => {
                                                    setDepositDate(e.target.value);
                                                    handleChange(e.target.name, e.target.value);
                                                }}
                                                style={{ width: "100%" }}
                                                InputLabelProps={{ shrink: true }}
                                                error={errors.depositDate}
                                                required
                                            />
                                        </TableCell>
                                        <TableCell style={{ ...tableCell }}>Recency:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            {donationData.RECENCY_DESCRIPTION}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Mailing Date:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            {donationData.MAILING_DATE
                                                ? moment(donationData.MAILING_DATE).format("l")
                                                : null}
                                        </TableCell>
                                        <TableCell style={{ ...tableCell }}>Mailing:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            {donationData.MAILING_DESCRIPTION}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Classification:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            {donationData.CLASSIFICATION_DESCRIPTION}
                                        </TableCell>
                                        <TableCell style={{ ...tableCell }}>Letter Package:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            {donationData.LETTER_PACKAGE_DESCRIPTION}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Matching Gift Ind:</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donationData.MATCHING_GIFT_IND}</TableCell>
                                        <TableCell style={{ ...tableCell }}>Letter Text:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            {donationData.LETTER_TEXT_DESCRIPTION}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Ignore Ind:</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donationData.IGNORE_IND}</TableCell>
                                        <TableCell style={{ ...tableCell }}>Monetary/List Code:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            {donationData.MONETARY_LIST_CODE_DESCRIPTION}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ ...tableCell }}>Old Appeal:</TableCell>
                                        <TableCell style={{ ...tableCell }}>{donationData.OLD_APPEAL}</TableCell>
                                        <TableCell style={{ ...tableCell }}>Frequency:</TableCell>
                                        <TableCell style={{ ...tableCell }}>
                                            {donationData.FREQUENCY_DESCRIPTION}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                ) : (
                    <p>{"No Donation Data to display."}</p>
                )}
            </div>
        </div>
    );
};

export default DonationDataForm;
